import {
	CommonActionTypes,
	DmData,
	DMExplorer,
	GenerateDmModel,
	IDmData,
	MessageBox,
	MessageBoxButton,
	MessageBoxButtons,
	Mobile,
	ServiceList
} from '@bimser/common';
import dmInitializerAction from '@bimser/common/dist/action/dmInitializerAction';
import { BCNotification, BCScrollbars, findMessage } from '@bimser/components';
import { IFormItemsLoadAction } from '@bimser/components/dist/BCLayoutCreator/common/entities';
import {
	base64ToBlobData,
	createGuid,
	extend,
	FormItemTypes,
	IDictionary,
	IItem,
	isImageFile,
	isNullOrUndefined,
	MLHelper
} from '@bimser/core';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import { JSX } from 'react';
import ClassNames from '../../../../assets/RDStyle.scss';
import { IUserEvent } from '../../../../entities/services';
import { FileToBase64 } from '../../../../helper';
import { GetDownloadUrl } from '../../../../services';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import {
	IControlExecutedServerEvent,
	ILazyLoadingEventArgs,
	IRDFileAddEventArgs,
	IRDFileAddServerEventArgs,
	IRDFileRemoveEventArgs,
	IRDFileUploadEventArgs
} from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import RDCategory from './components/RDCategory';
import {
	IRelatedDocumentCategory,
	IRelatedDocumentEventType,
	IRelatedDocumentFile,
	IRelatedDocumentFilePreviewInfo,
	IRelatedDocumentsProperties,
	RelatedDocumentAddingMode,
	RelatedDocumentsViewType
} from './entities';
import { getDownloadUrl } from './helper/getAllowedExtensionsOptions';

export default class RelatedDocuments extends CompositeDataBoundControl {
	categories: Array<IRelatedDocumentCategory>;
	files: Array<IRelatedDocumentFile>;
	addingMode: RelatedDocumentAddingMode;
	addNewEnabled: boolean;
	deleteEnabled: boolean;
	viewable: boolean;
	createEnabled: boolean;
	path: string;
	showCategoryDescription: boolean;
	showFileDescription: boolean;
	fileDescriptionIsRequired: boolean;
	showContentForImageFiles: boolean;
	showCreateDate: boolean;
	showPath: boolean;
	showCreator: boolean;
	showFileExtension: boolean;
	dmSelectedObjects: any[];
	addNewBtnItems: IItem[];
	defaultAddNewItem: string;
	targetId: string;
	targetSecretKey: string;
	viewType: RelatedDocumentsViewType;
	filesToUpload: { id: string; file: File; categoryId: string }[];
	filesToRunEvent: IRelatedDocumentFile[];
	enabledOCR: boolean;
	advancedEdit: boolean;
	viewPanelSize: number;

	__rdCategoryRef: RDCategory;

	constructor() {
		super();
		this.onFileAddedForUpload = this.onFileAddedForUpload.bind(this);
		this.onFileListEventClick = this.onFileListEventClick.bind(this);
		this.openDMSelector = this.openDMSelector.bind(this);
		this.openLocalSelector = this.openLocalSelector.bind(this);
		this.onAddEvent = this.onAddEvent.bind(this);
		this.renderRDCategory = this.renderRDCategory.bind(this);
		this.renderContent = this.renderContent.bind(this);
		this.runLazyLoadEvent = this.runLazyLoadEvent.bind(this);
		this.compareFiles = this.compareFiles.bind(this);

		this.controlType = FormItemTypes.EControlTypes.RelatedDocuments;
		this.caption.position = 'top';
		this.categories = [];
		this.files = [];
		this.addingMode = RelatedDocumentAddingMode.All;
		this.viewType = RelatedDocumentsViewType.Card;
		this.addNewEnabled = true;
		this.deleteEnabled = true;
		this.viewable = true;
		this.createEnabled = true;
		this.showCategoryDescription = false;
		this.showFileDescription = false;
		this.fileDescriptionIsRequired = false;
		this.showContentForImageFiles = false;
		this.showCreateDate = false;
		this.showPath = true;
		this.showCreator = false;
		this.showFileExtension = true;
		this.path = '';
		this.defaultAddNewItem = RelatedDocumentAddingMode.DM.toString();
		this.addNewBtnItems = [
			{
				key: RelatedDocumentAddingMode.Local.toString(),
				text: findMessage.get('101386'),
				value: RelatedDocumentAddingMode.Local,
				selected: false
			},
			{
				key: RelatedDocumentAddingMode.DM.toString(),
				text: findMessage.get('101387'),
				value: RelatedDocumentAddingMode.DM,
				selected: true
			}
		];
		this.filesToUpload = [];
		this.filesToRunEvent = [];
		this.targetId = null;
		this.targetSecretKey = '';
		this.enabledOCR = false;
		this.advancedEdit = false;
		this.viewPanelSize = 1;

		this.subscribe('trigger:OnRDFileSelected', value => {
			const { files, categoryId } = JSON.parse(value);

			const data = files.map(file => {
				const datum = base64ToBlobData(file.data);
				const blob = new Blob([datum], { type: file.mimeType });
				return new File([blob], file.name, { type: file.mimeType });
			});

			this.onFileAddedForUpload(data, categoryId, true);
		});

		this.subscribe('trigger:OnOpenFileSelector', (data: any) => {
			const { addKey, categoryId } = JSON.parse(data);
			this.onAddEvent(addKey, categoryId);
		});

		this.subscribe('trigger:OnDeleteFile', (data: any) => {
			const { eventKey, fileId } = JSON.parse(data);
			this.onFileListEventClick(fileId, eventKey);
		});

		this.subscribe('trigger:OnChangeFileDescription', (data: any) => {
			const { fileId, description } = JSON.parse(data);
			let newFiles: IRelatedDocumentFile[] = extend(true, true, [], this.files);
			newFiles = newFiles.map(f => {
				if (f.id === fileId) {
					f.description = description;
				}
				return f;
			});
			this.__proxyChanging.files = newFiles;
		});

		this.subscribe('trigger:RelatedDocumentsFileUploaded', (data: string) => {
			const files: { createFileResponse: any; res: any; fileId: string }[] = JSON.parse(data);
			files.forEach(file => {
				const { createFileResponse, res, fileId } = file;
				let dmData: DmData = GenerateDmModel(createFileResponse);
				dmData = dmData.set('path', res.paths[dmData.id]);
				const payload = {
					payload: {
						windowId: this.__form.__windowId,
						controlId: this.name,
						id: fileId,
						dmData: dmData,
						success: true
					}
				};
				this.__form.emit('event:DMFileUploaded', payload);
			});
		});

		this.subscribe('trigger:RelatedDocumentsDmSelectionResolved', (data: string) => {
			const payload = JSON.parse(data);
			const dmData = payload.dmData.map((file: any) => ({
				id: file.id,
				name: MLHelper.getMLText(file.name),
				metadata: { OWNER: file.creator },
				secretKey: file.secretKey,
				versionSecretKey: file.versionSecretKey,
				path: file.path
			}));

			payload.success = true;
			payload.dmData = dmData;

			this.__onFileSelected(payload);
		});
	}

	async setOCRData(file: IRelatedDocumentFile, secretKey: string, versionSecretKey: string) {
		if (this.enabledOCR) {
			file.ocrData = await ServiceList.dm.DocumentManagement.Enrichment.OcrData.callPromise({
				secretKey,
				versionSecretKey
			});
		}
	}

	initialize() {
		super.initialize();

		this.__form.subscribe('event:DMFileUploading', async (data: any) => {
			if (
				!(
					data.payload.windowId == this.__form.__windowId &&
					data.payload.controlId == this.name
				)
			) {
				return;
			}

			data.payload.onBefore(async (_file: File) => {
				const [newFile] = await this.fireControlEvent('OnBeforeFileUpload', true, _file); //Client Event

				if (newFile instanceof File) {
					const oldFileIndex = this.filesToUpload.findIndex(
						file => file.id === data.payload.id
					);
					if (oldFileIndex != -1) {
						this.filesToUpload[oldFileIndex] = {
							...this.filesToUpload[oldFileIndex],
							file: newFile
						};
					}

					return newFile;
				}
			});
		});

		this.__form.subscribe('event:DMFileUploaded', async (data: any) => {
			if (
				!(
					data?.payload?.windowId == this.__form.__windowId &&
					data.payload.controlId == this.name
				)
			) {
				return;
			}
			const res = data.payload;
			for (const file of this.filesToUpload) {
				if (file.id != res.id) {
					continue;
				}
				if (res.success) {
					const category = this.categories.find(c => c.categoryId == file.categoryId);

					const _file: IRelatedDocumentFile = {
						id: res.dmData.id,
						key: file.id,
						name: file.file.name,
						createDate: dayjs().toISOString(),
						creatorId: res.dmData.createdBy?.id,
						creator: res.dmData.metadata['OWNER'],
						secretKey: res.dmData.secretKey,
						path: res.dmData.path,
						extension: this.extractExtensionFromFileName(file.file.name),
						fileSize: file.file.size,
						category: category || this.categories[0],
						versionSecretKey: res.dmData.versionSecretKey
					};

					await this.setOCRData(_file, res.dmData.secretKey, res.dmData.versionSecretKey);

					await this.fireControlEvent('OnBeforeFileAdd', true, _file); //Client Event
					this.filesToUpload = this.filesToUpload.filter(f => f.id != file.id);
					this.filesToRunEvent.push(_file);
					await this.fireControlEvent('OnAfterFileAdd', true, _file); //Client Event
				} else {
					this.filesToUpload = this.filesToUpload.filter(f => f.id != file.id);
				}
			}
			if (this.filesToUpload.length == 0 && this.filesToRunEvent.length > 0) {
				await this.fireControlEvent('OnBeforeFileAdd', false, this.filesToRunEvent); //Server Event

				const returnValue = this.GetFileDownloadURL([
					...this.files,
					...this.filesToRunEvent
				]);
				const filesToRunEvent = [...this.filesToRunEvent];

				if (typeof returnValue?.then === 'function') {
					returnValue.then(() => {
						this.fireControlEvent('OnAfterFileAdd', false, filesToRunEvent);
					});
				} else {
					await this.fireControlEvent('OnAfterFileAdd', false, filesToRunEvent);
				}
				this.filesToRunEvent = [];
			}
			this.__proxyChanging.loading = false;
		});
	}

	private async __onFileSelected(payload: { dmData: IDmData[]; categoryId: string }) {
		const category = this.categories.find(c => c.categoryId == payload.categoryId);
		let allowedFilesControl = false;

		if (category.allowedFileExtensions?.length > 0) {
			const fileLength = payload.dmData.length;
			const allowedFiles = payload.dmData.filter((t: any) =>
				category.allowedFileExtensions.includes(
					t.name.slice(t.name.lastIndexOf('.') + 1).toUpperCase()
				)
			);
			if (fileLength != allowedFiles.length) {
				BCNotification.warning({
					message: findMessage.get('101357')
				});
				Mobile.MobileView.relatedDocumentsFilesToUploadInfo([]);
				allowedFilesControl = true;
			}
		}

		if (!allowedFilesControl) {
			const files = [...this.files];
			for (const dmObj of payload.dmData) {
				if (this.files.findIndex(f => f.id == dmObj.id) == -1) {
					const _file: IRelatedDocumentFile = {
						id: dmObj.id,
						key: dmObj.id,
						name: dmObj.name,
						createDate: dayjs().toISOString(),
						creatorId: dmObj.createdBy?.id,
						creator: dmObj.metadata['OWNER'],
						path: dmObj.path,
						secretKey: dmObj.secretKey,
						extension: this.extractExtensionFromFileName(dmObj.name),
						category: category || this.categories[0],
						versionSecretKey: dmObj.versionSecretKey
					};

					await this.setOCRData(_file, dmObj.secretKey, dmObj.versionSecretKey);

					await this.fireControlEvent('OnBeforeFileAdd', true, _file); //Client Event
					files.push(_file);
					this.filesToRunEvent.push(_file);
					await this.fireControlEvent('OnAfterFileAdd', true, _file); //Client Event
				}
			}

			if (this.filesToRunEvent.length > 0) {
				await this.fireControlEvent('OnBeforeFileAdd', false, this.filesToRunEvent); //Server Event
				await this.fireControlEvent('OnAfterFileAdd', false, this.filesToRunEvent); //Server Event
				this.filesToRunEvent = [];
			}

			await this.GetFileDownloadURL(Array.from(files));
		}
	}

	private async GetFileDownloadURL(files: Array<IRelatedDocumentFile>) {
		if (!(files && files.length > 0) || this.__form.displayMode == 'preview') {
			return;
		}
		const url =
			(window as any).serviceConfig.services.dm.baseURL +
			'/DocumentManagement/Objects/GetDownloadUrl';
		const _files = files.filter(t => !t.downloadUrl);

		if (_files.length == 0) {
			this.__proxyChanging.files = Array.from(files);
			Mobile.MobileView.relatedDocumentsLoadingStatus(false);
			return;
		}

		try {
			const responses = await this.downloadAsync(url, _files);
			for (const response of responses) {
				const fileIndex = files.findIndex(
					t_1 => t_1.id == response.id && t_1.secretKey == response.secretKey
				);
				if (fileIndex != -1) {
					files[fileIndex].downloadUrl = response.downloadUrl;
					files[fileIndex].mimeType = response.mimeType;
				}
			}
			Mobile.MobileView.relatedDocumentsLoadingStatus(false);
			this.__proxyChanging.files = Array.from(files);
		} catch (err) {
			console.log('RelatedDocuments -> GetFileDownloadURL -> err', err);
		}
	}

	private downloadAsync(url: string, files: IRelatedDocumentFile[]) {
		const awaitPromises: Array<Promise<IRelatedDocumentFilePreviewInfo>> = [];
		for (const file of files) {
			if (isNullOrUndefined(file.secretKey) || !file.secretKey) {
				continue;
			}

			const param: any = {
				secretKey: file.secretKey
			};

			awaitPromises.push(
				new Promise<IRelatedDocumentFilePreviewInfo>((resolve, reject) => {
					GetDownloadUrl(url, param)
						.then((response: any) => {
							const resolveData: IRelatedDocumentFilePreviewInfo = {
								id: file.id,
								name: file.name,
								secretKey: file.secretKey,
								categoryId: file.category.categoryId,
								downloadUrl: response.data.downloadUrl,
								mimeType: response.data.mimeType
							};
							resolve(resolveData);
						})
						.catch(e => {
							reject(e);
						});
				})
			);
		}

		return new Promise<Array<IRelatedDocumentFilePreviewInfo>>((resolve, reject) => {
			Promise.all(awaitPromises)
				.then(result => {
					resolve(result);
				})
				.catch(err => reject(err));
		});
	}

	public runLazyLoadEvent() {
		const eventData: IUserEvent<ILazyLoadingEventArgs> = {
			argument: {},
			controlId: this.name,
			name: 'LazyLoading',
			sender: 'web',
			runImmediately: true,
			guid: createGuid()
		};

		if (this.__form && this.__form.displayMode != 'preview') {
			this.__proxyChanging.loading = true;

			setTimeout(() => {
				this.__form.__runServerEvent(eventData);
			}, 1);
		}
	}

	__loadProperties(data: IRelatedDocumentsProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.categories = data.categories;
		this.__proxyChanged.files = data.files ? data.files : [];
		this.__proxyChanged.addingMode = data.addingMode;
		this.__proxyChanged.addNewEnabled = data.addNewEnabled;
		this.__proxyChanged.deleteEnabled = data.deleteEnabled;
		this.__proxyChanged.viewable = data.viewable;
		this.__proxyChanged.createEnabled = data.createEnabled;
		this.__proxyChanged.showCategoryDescription = data.showCategoryDescription;
		this.__proxyChanged.fileDescriptionIsRequired = data.fileDescriptionIsRequired;
		this.__proxyChanged.showFileDescription = data.showFileDescription;
		this.__proxyChanged.showContentForImageFiles = data.showContentForImageFiles;
		this.__proxyChanged.showCreateDate = data.showCreateDate;
		this.__proxyChanged.showPath = data.showPath;
		this.__proxyChanged.showCreator = data.showCreator;
		this.__proxyChanged.showFileExtension = data.showFileExtension;
		this.__proxyChanged.path = data.path;
		this.__proxyChanged.targetId = data.targetId;
		this.__proxyChanged.targetSecretKey = data.targetSecretKey;
		this.__proxyChanged.loading = data.loading;
		this.__proxyChanged.viewType = data.viewType || RelatedDocumentsViewType.Card;
		this.__proxyChanged.enabledOCR = data.enabledOCR;
		this.__proxyChanged.advancedEdit = data.advancedEdit;
		this.__proxyChanged.viewPanelSize = data.viewPanelSize;

		if (
			this.showContentForImageFiles &&
			data.files &&
			data.files.filter(
				(t: IRelatedDocumentFile) =>
					isImageFile(t.extension) && !t.downloadUrl && t.secretKey
			).length > 0
		) {
			this.GetFileDownloadURL(data.files);
		}
	}

	private extractExtensionFromFileName(filename: string) {
		return filename?.split('.')?.pop()?.toLowerCase() || '';
	}

	onFileListEventClick(fileId: string, eventKey: string, afterEvent?: () => void) {
		const file = this.files.find(f => f.id == fileId);

		if (file) {
			switch (eventKey) {
				case 'unlinkMobile': {
					const argument: IRDFileRemoveEventArgs = {
						name: this.name,
						file,
						cancel: false
					};

					this.fireControlEvent('OnBeforeFileRemove', true, file, argument).then(() => {
						if (!argument.cancel) {
							this.fireControlEvent('OnBeforeFileRemove', false, file).then(
								(
									res: [
										{
											loadActionData: IFormItemsLoadAction;
											executedServerEvent: IControlExecutedServerEvent;
										}
									]
								) => {
									if (!res?.[0]?.executedServerEvent?.parameters?.cancel) {
										this.__proxyChanging.files = this.files.filter(
											f => f.id != fileId
										);

										this.fireControlEvent('OnAfterFileRemove', true, file);
										this.fireControlEvent('OnAfterFileRemove', false, file);
									}
								}
							);
						}
					});
					break;
				}
				case 'unlink':
					if (this.deleteEnabled && fileId && typeof fileId == 'string') {
						MessageBox.warning({
							title: findMessage.get('101394'),
							content: findMessage
								.get('101395')
								.replace('$0', file.name || findMessage.get('100810')),
							buttons: MessageBoxButtons.YesNo
						}).then(res => {
							if (res.button.id == MessageBoxButton.Yes) {
								const argument: IRDFileRemoveEventArgs = {
									name: this.name,
									file,
									cancel: false
								};

								this.fireControlEvent(
									'OnBeforeFileRemove',
									true,
									file,
									argument
								).then(() => {
									if (!argument.cancel) {
										this.fireControlEvent(
											'OnBeforeFileRemove',
											false,
											file,
											argument
										).then(
											(
												res: [
													{
														loadActionData: IFormItemsLoadAction;
														executedServerEvent: IControlExecutedServerEvent;
													}
												]
											) => {
												if (
													!res?.[0]?.executedServerEvent?.parameters
														?.cancel
												) {
													this.__proxyChanging.files = this.files.filter(
														f => f.id != fileId
													);

													this.fireControlEvent(
														'OnAfterFileRemove',
														true,
														file
													);
													this.fireControlEvent(
														'OnAfterFileRemove',
														false,
														file
													);
												}
											}
										);
									}
								});
							}
						});
					}
					break;
				case 'download':
					if (file.secretKey == null) {
						BCNotification.warning({
							message: findMessage.get('101178')
						});
					} else {
						this.__form.emit(
							'dispatchAction',
							dmInitializerAction({
								onCompleted: () => {
									this.__form.emit('dispatchAction', {
										type: CommonActionTypes.RELATED_DOCUMENTS_DOWNLOAD_FILE,
										payload: {
											secretKey: file.secretKey,
											fileName: file.name
										}
									});
								}
							})
						);
					}
					break;
				case 'view':
					this.openFile(file);
					break;
				case 'enterDescription': {
					if (file.secretKey == null) {
						BCNotification.warning({
							message: findMessage.get('100827')
						});
					} else if (this.enabled && this.clientEnabled && !this.__isReadOnly()) {
						this.openFileDescriptionInput(file);
					}
					break;
				}
				default:
					break;
			}

			afterEvent?.();
		}
	}

	getThumbnail(file: any) {
		return (
			<img
				src={getDownloadUrl(file.downloadUrl)}
				height="48px"
				width="48px"
				alt={file.name}
			/>
		);
	}

	private openFileDescriptionInput(_file: IRelatedDocumentFile) {
		if (this.__form.displayMode == 'preview') {
			BCNotification.warning({
				message: findMessage.get('101392')
			});
			return;
		}
		const messageBoxId = createGuid();
		let text = _file.description;
		this.__form.__openedModalList.push(messageBoxId);

		const { close } = this.__form.__interactionHelper({
			id: messageBoxId,
			type: 'modal',
			title: findMessage.get('101397'),
			width: '500px',
			contentComponentType: 'FileDescriptionInput',
			contentComponentProps: {
				fileName: _file.name,
				fileDownloadUrl: _file.downloadUrl,
				description: text,
				thumbnail: this.getThumbnail(_file),
				onChange: description => {
					text = description;
				}
			},
			onOk: () => {
				let newFiles: IRelatedDocumentFile[] = extend(true, true, [], this.files);
				newFiles = newFiles.map(f => {
					if (f.id === _file.id) {
						f.description = text;
					}
					return f;
				});
				this.__proxyChanging.files = newFiles;
				close();
			}
		});
	}

	private compareFiles(data: IRelatedDocumentFile[]) {
		this.__form.emit(
			'dispatchAction',
			dmInitializerAction({
				onCompleted: () => {
					this.__form.emit('dispatchAction', {
						type: CommonActionTypes.RELATED_DOCUMENTS_COMPARE_FILES,
						payload: data
					});
				}
			})
		);
	}

	private openLocalSelector(categoryId: string) {
		const category = this.categories.find(c => c.categoryId == categoryId);
		const categoryFiles = this.files.filter(
			f => f.category.categoryId == category.categoryId
		).length;
		if (category.maxFilesCount !== null && category.maxFilesCount == categoryFiles) {
			BCNotification.warning({
				message: findMessage.get('101399')
			});
			Mobile.MobileView.relatedDocumentsFilesToUploadInfo([]);
			return;
		}

		const _targetSecretKey = category?.targetSecretKey || this.targetSecretKey;
		const _targetId = category?.targetId || this.targetId;

		if (_targetSecretKey?.length > 0) {
			const modalId = 'RD.ADD_ITEM_FROM_LOCAL_SELECTOR';
			const handleOk = (_items: File[]) => {
				if (this.filesToUpload && this.filesToUpload.length > 0) {
					this.__proxyChanging.loading = true;

					this.__form.emit(
						'dispatchAction',
						dmInitializerAction({
							onCompleted: () => {
								this.__form.emit('dispatchAction', {
									type: CommonActionTypes.RELATED_DOCUMENTS_UPLOAD_FILE_ACTION,
									payload: {
										eventEmitter: true,
										controlId: this.name,
										windowId: this.__form.__windowId,
										destinationSecret: _targetSecretKey,
										files: this.filesToUpload,
										targetId: _targetId,
										hasBeforeCallback: true
									}
								});
							}
						})
					);
				}
			};
			this.__form.__openedModalList.push(modalId);

			const { close } = this.__form.__interactionHelper({
				id: modalId,
				type: 'modal',
				title: findMessage.get('101202'),
				width: this.__form.__fromMobile
					? `${this.__form.windowSize.width}px !important`
					: '546px ',
				contentComponentType: 'AddNewFileLocalModal',
				contentComponentProps: {
					selectionLimit: category.maxFilesCount
						? Math.max(category.maxFilesCount - categoryFiles, 0)
						: null,
					fileDescriptionIsRequired: this.fileDescriptionIsRequired,
					autoClose: true,
					onFileAddedForUpload: this.onFileAddedForUpload,
					onCloseClick: () => {
						close();
					},
					onOkClick: handleOk,
					mobileFormView: this.__form.__mobileFormView,
					allowedFileExtensions: category.allowedFileExtensions,
					maxFileSize: category.maxFileSize,
					categoryId: category.categoryId
				},
				modalProps: {
					props: {
						footer: null,
						disableScroll: true,
						bodyStyle: this.__form.__fromMobile
							? {
									verticalAlign: 'unset'
							  }
							: {}
					}
				},
				onOk: () => {
					close();
				}
			});
		} else {
			BCNotification.warning({
				message: findMessage.get('101311')
			});
		}
	}

	private openDMSelector(categoryId: string) {
		const category = this.categories.find(c => c.categoryId == categoryId);
		const categoryFiles = this.files.filter(
			f => f.category.categoryId == category.categoryId
		).length;

		const extensions = category.allowedFileExtensions.map(ext => ext.toLowerCase());

		const modalId = 'RD.ADD_ITEM_FROM_DM_SELECTOR';
		this.__form.__openedModalList.push(modalId);

		const { close } = this.__form.__interactionHelper({
			id: modalId,
			type: 'modal',
			title: extensions?.length ? findMessage.get('103605') : findMessage.get('103717'),
			contentComponentType: 'AddNewFileDMModal',
			contentComponentProps: {
				modalId,
				extensions: extensions.length > 0 ? extensions : ['*'],
				maxFileCount: category.maxFilesCount
					? Math.max(category.maxFilesCount - categoryFiles, 0)
					: null,
				minFileCount: category.minFilesCount,
				onOkEvent: async (items: DMExplorer.Entities.INavigationItem[]) => {
					this.__onFileSelected({
						categoryId,
						dmData: items.map(item => item.baseItem)
					});
				}
			},
			modalProps: {
				props: {
					footer: null,
					disableScroll: true
				}
			},
			onOk: () => {
				close();
			}
		});
	}

	private onAddEvent(addKey: RelatedDocumentAddingMode, categoryId: string) {
		if (!(this.enabled && this.clientEnabled && !this.__isReadOnly() && !this.__isDisabled())) {
			return;
		}
		if (this.__form.displayMode == 'preview') {
			BCNotification.warning({
				message: findMessage.get('101392')
			});
		} else if (addKey == RelatedDocumentAddingMode.DM) {
			this.openDMSelector(categoryId);
		} else {
			this.openLocalSelector(categoryId);
		}
	}

	private async onFileAddedForUpload(files: File[], categoryId: string, isDrop = false) {
		if (files.length > 0 && this.__form.displayMode != 'preview') {
			for (const file of files) {
				this.filesToUpload.push({
					id: createGuid(),
					categoryId,
					file
				});
			}

			const category = this.categories.find(t => t.categoryId == categoryId);
			const _targetSecretKey = category?.targetSecretKey || this.targetSecretKey;
			const _targetId = category?.targetId || this.targetId;

			if (isDrop && this.filesToUpload?.length > 0 && _targetSecretKey?.length > 0) {
				this.__form.emit(
					'dispatchAction',
					dmInitializerAction({
						onCompleted: () => {
							this.__form.emit('dispatchAction', {
								type: CommonActionTypes.RELATED_DOCUMENTS_UPLOAD_FILE_ACTION,
								payload: {
									eventEmitter: true,
									controlId: this.name,
									windowId: this.__form.__windowId,
									destinationSecret: _targetSecretKey,
									files: this.filesToUpload,
									targetId: _targetId,
									hasBeforeCallback: true
								}
							});
						}
					})
				);
			} else if (isDrop && _targetSecretKey?.length == 0) {
				BCNotification.warning({
					message: findMessage.get('101311')
				});
			}

			if (
				this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
			) {
				try {
					this.filesToUpload = await Promise.all(
						this.filesToUpload.map(async ({ file, id, categoryId }) => {
							if (!(file instanceof File)) {
								file = new File(
									[base64ToBlobData((file as any).content)],
									(file as any).name,
									{ type: (file as any).type }
								);
							}

							const [newFile] = await this.fireControlEvent(
								'OnBeforeFileUpload',
								true,
								file
							); //Client Event

							return { id, categoryId, file: newFile || file };
						})
					);

					const mobileFiles = await Promise.all(
						this.filesToUpload.map(async ({ file, id, categoryId }) => ({
							id: id,
							categoryId: categoryId,
							file: {
								name: file.name,
								type: file.type,
								size: file.size,
								content: await FileToBase64(file)
							}
						}))
					);

					Mobile.MobileView.relatedDocumentsFilesToUploadInfo(mobileFiles);
				} catch (err) {
					console.log(err);
				} finally {
					Mobile.MobileView.relatedDocumentsFilesToUploadInfo(this.filesToUpload);
				}
			}
		}
	}

	fireControlEvent(
		event: IRelatedDocumentEventType,
		isClientEvent: boolean,
		file: (IRelatedDocumentFile | IRelatedDocumentFile[]) | (File | File[]),
		_argument?: IRDFileAddEventArgs | IRDFileRemoveEventArgs | IRDFileUploadEventArgs
	) {
		const argument: typeof _argument = _argument || {
			name: this.name,
			file: file as IRelatedDocumentFile
		};

		switch (event) {
			case 'OnBeforeFileUpload': {
				if (isClientEvent) {
					return this.emit('event:OnBeforeFileUpload', argument);
				}
				return;
			}
			case 'OnBeforeFileAdd': {
				if (isClientEvent) {
					return this.emit('event:OnBeforeFileAdd', argument);
				}

				return this.emit<IUserEvent<IRDFileAddServerEventArgs>>(
					'event:server:OnBeforeFileAdd',
					{
						argument: {
							name: this.name,
							files: file as IRelatedDocumentFile[]
						},
						controlId: this.name,
						name: 'OnBeforeFileAdd',
						sender: 'web',
						runImmediately: true,
						guid: createGuid()
					}
				);
			}
			case 'OnAfterFileAdd': {
				if (isClientEvent) {
					return this.emit('event:OnAfterFileAdd', argument);
				}

				return this.emit<IUserEvent<IRDFileAddServerEventArgs>>(
					'event:server:OnAfterFileAdd',
					{
						argument: {
							name: this.name,
							files: file as IRelatedDocumentFile[]
						},
						controlId: this.name,
						name: 'OnAfterFileAdd',
						sender: 'web',
						runImmediately: true,
						guid: createGuid()
					}
				);
			}
			case 'OnBeforeFileRemove': {
				if (isClientEvent) {
					return this.emit(
						'event:OnBeforeFileRemove',
						argument as IRDFileRemoveEventArgs
					);
				}

				return this.emit<IUserEvent<IRDFileRemoveEventArgs>>(
					'event:server:OnBeforeFileRemove',
					{
						argument: argument as IRDFileRemoveEventArgs,
						controlId: this.name,
						name: 'OnBeforeFileRemove',
						sender: 'web',
						runImmediately: true,
						guid: createGuid()
					}
				);
			}
			case 'OnAfterFileRemove': {
				if (isClientEvent) {
					return this.emit('event:OnAfterFileRemove', argument as IRDFileRemoveEventArgs);
				}

				return this.emit<IUserEvent<IRDFileRemoveEventArgs>>(
					'event:server:OnAfterFileRemove',
					{
						argument: argument as IRDFileRemoveEventArgs,
						controlId: this.name,
						name: 'OnAfterFileRemove',
						sender: 'web',
						runImmediately: true,
						guid: createGuid()
					}
				);
			}
		}
	}

	addFile(categoryId: string) {
		this.onAddEvent(this.addingMode, categoryId);
	}

	copyFiles(categoryId: string, files: typeof this.files) {
		const newFiles = [...this.files];

		const category = this.categories.find(c => c.categoryId === categoryId);
		if (!category) {
			return;
		}

		for (const file of files) {
			const newFile = cloneDeep(file);
			newFile.category = cloneDeep(category);
			newFiles.push(newFile);
		}

		this.__proxyChanging.files = newFiles;
	}

	openFile(file: IRelatedDocumentFile) {
		if (file.secretKey == null) {
			BCNotification.warning({
				message: findMessage.get('100827')
			});
		} else if (this.viewable) {
			this.__form.emit(
				'dispatchAction',
				dmInitializerAction({
					onCompleted: () => {
						this.__form.emit('dispatchAction', {
							type: CommonActionTypes.RELATED_DOCUMENTS_VIEW_FILE,
							payload: {
								windowId: this.__form.__windowId,
								panelId: true,
								parentWindowType: this.__form.parentWindowType,
								secretKey: file.secretKey,
								fileName: file.name,
								itemId: file.id,
								openOn: this.__form.openOn || 'panel',
								editable: this.advancedEdit,
								panelSize: this.viewPanelSize,
								maskClosable: false
							}
						});
					}
				})
			);
		}
	}

	__onFormResize(): void {
		this.__rdCategoryRef?.rdButtonTypeRef?.onFormResize();
	}

	renderRDCategory() {
		return (
			<>
				{this.categories.map(c => {
					const files = this.files.filter(f => f.category.categoryId == c.categoryId);
					return (
						<RDCategory
							key={c.categoryId}
							ref={ref => (this.__rdCategoryRef = ref)}
							isPreviewMode={this.__form.displayMode == 'preview'}
							addingMode={this.addingMode}
							onFileListEventClick={this.onFileListEventClick}
							files={files}
							category={c}
							onAddClick={this.onAddEvent}
							onFileDrop={this.onFileAddedForUpload}
							currentLanguage={this.__form.currentLanguage}
							viewType={this.viewType}
							enabled={c.enabled}
							onCompareFiles={this.compareFiles}
							options={{
								fileDescriptionIsRequired: this.fileDescriptionIsRequired,
								addNewEnabled: !this.__isDisabled() && this.addNewEnabled,
								deleteEnabled:
									!this.__isDisabled() &&
									!this.__isReadOnly() &&
									c.enabled &&
									this.deleteEnabled,
								viewable: this.viewable,
								createEnabled:
									this.__isDisabled() &&
									!this.__isReadOnly() &&
									this.createEnabled,
								showCategoryDescription: this.showCategoryDescription,
								showFileDescription: this.showFileDescription,
								showContentForImageFiles: this.showContentForImageFiles,
								showPath: this.showPath,
								showCreateDate: this.showCreateDate,
								showCreator: this.showCreator,
								showFileExtension: this.showFileExtension,
								disabled: this.__isDisabled(),
								readOnly: this.__isReadOnly(),
								fileOptionsPopupContainer: this.__form.__getShadowRootScrollable()
							}}
						/>
					);
				})}
			</>
		);
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			categories: this.categories,
			files: this.files,
			addingMode: this.addingMode,
			addNewEnabled: this.addNewEnabled,
			deleteEnabled: this.deleteEnabled,
			viewable: this.viewable,
			createEnabled: this.createEnabled,
			path: this.path,
			showCategoryDescription: this.showCategoryDescription,
			showFileDescription: this.showFileDescription,
			fileDescriptionIsRequired: this.fileDescriptionIsRequired,
			showContentForImageFiles: this.showContentForImageFiles,
			showCreateDate: this.showCreateDate,
			showPath: this.showPath,
			showCreator: this.showCreator,
			showFileExtension: this.showFileExtension,
			dmSelectedObjects: this.dmSelectedObjects,
			addNewBtnItems: this.addNewBtnItems,
			defaultAddNewItem: this.defaultAddNewItem,
			targetId: this.targetId,
			targetSecretKey: this.targetSecretKey,
			viewType: this.viewType,
			filesToUpload: this.filesToUpload,
			filesToRunEvent: this.filesToRunEvent,
			enabledOCR: this.enabledOCR,
			advancedEdit: this.advancedEdit,
			viewPanelSize: this.viewPanelSize
		};
	}

	renderContent() {
		return (
			<BCScrollbars
				styles={
					this.viewType === RelatedDocumentsViewType.Compact
						? { maxHeight: '320px' }
						: undefined
				}
			>
				{this.renderRDCategory()}
			</BCScrollbars>
		);
	}

	__render(_children?: JSX.Element) {
		if (this.viewType === RelatedDocumentsViewType.Button) {
			return super.__render(this.renderRDCategory());
		}

		return super.__render(
			<div
				className={[
					ClassNames.RelatedDocumentsContent,
					Styles.FillBody,
					this.viewType == RelatedDocumentsViewType.Compact
						? ClassNames.compactCategory
						: ''
				].join(' ')}
				style={{ '--bgcolor': this.style.backgroundColor } as any}
			>
				<div className={ClassNames.CategoryWrapper}>{this.renderContent()}</div>
			</div>
		);
	}
}
